import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Brain, BarChart3, Leaf, Cpu, Lightbulb, TrendingUp, Zap, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QuoteModal from '../components/modals/QuoteModal';

const SmartBuildingPage = () => {
  const navigate = useNavigate();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const aiSolutions = [
    {
      icon: Brain,
      title: 'Predictive Analytics',
      description: 'AI-powered demand forecasting and material requirement prediction for optimized procurement',
      capabilities: ['Demand Forecasting', 'Price Prediction', 'Supply Risk Analysis', 'Market Trends']
    },
    {
      icon: BarChart3,
      title: 'Supply Chain Optimization',
      description: 'Machine learning algorithms to optimize supplier selection and procurement timing',
      capabilities: ['Supplier Scoring', 'Route Optimization', 'Inventory Management', 'Cost Optimization']
    },
    {
      icon: Leaf,
      title: 'Sustainability Analytics',
      description: 'Environmental impact assessment and carbon footprint tracking for green construction',
      capabilities: ['Carbon Tracking', 'Sustainability Scoring', 'Green Material Matching', 'ESG Compliance']
    },
    {
      icon: Cpu,
      title: 'Smart Automation',
      description: 'Automated procurement workflows with intelligent decision-making capabilities',
      capabilities: ['Auto-Ordering', 'Smart Contracts', 'Quality Control', 'Compliance Monitoring']
    }
  ];

  const dataServices = [
    {
      title: 'Market Intelligence',
      description: 'Real-time construction material market data, pricing trends, and supply forecasts',
      icon: TrendingUp
    },
    {
      title: 'Performance Analytics',
      description: 'Comprehensive dashboards tracking supplier performance, delivery metrics, and quality scores',
      icon: BarChart3
    },
    {
      title: 'Energy Optimization',
      description: 'AI-driven recommendations for energy-efficient materials and sustainable building practices',
      icon: Zap
    },
    {
      title: 'Predictive Insights',
      description: 'Advanced analytics to predict project delays, material shortages, and cost overruns',
      icon: Eye
    }
  ];

  const sustainabilityFeatures = [
    {
      metric: 'Carbon Footprint Reduction',
      value: '35% Average',
      description: 'AI-optimized material selection reduces project carbon footprint'
    },
    {
      metric: 'Waste Reduction',
      value: '40% Less Waste',
      description: 'Precise quantity forecasting minimizes material waste'
    },
    {
      metric: 'Energy Efficiency',
      value: '25% Improvement',
      description: 'Smart material recommendations improve building energy performance'
    },
    {
      metric: 'Green Certification',
      value: '90% Success Rate',
      description: 'Projects achieve LEED, BREEAM, or other green building certifications'
    }
  ];

  const technologyStack = [
    { name: 'Machine Learning', category: 'AI/ML' },
    { name: 'IoT Sensors', category: 'Hardware' },
    { name: 'Blockchain', category: 'Security' },
    { name: 'Cloud Computing', category: 'Infrastructure' },
    { name: 'Big Data Analytics', category: 'Data Processing' },
    { name: 'API Integration', category: 'Connectivity' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-white hover:bg-gray-800 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-indigo-100 text-indigo-800">Smart Building Solutions</Badge>
              <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                AI & Data Analytics
                <span className="block font-medium">for Construction</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Harness the power of artificial intelligence and big data to optimize 
                material procurement, reduce costs, and build more sustainably.
              </p>
              <div className="flex space-x-4">
                <Button 
                  onClick={() => setIsQuoteModalOpen(true)}
                  size="lg" 
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  <Brain size={20} className="mr-2" />
                  Request AI Demo
                </Button>
                <Button size="lg" variant="outline">
                  View Analytics
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1518709268805-4e9042af2176?q=80&w=2125" 
                alt="Smart Building Technology"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* AI Solutions */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            AI-Powered Solutions
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {aiSolutions.map((solution, index) => {
              const IconComponent = solution.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <IconComponent size={24} className="text-indigo-600" />
                      </div>
                      <h3 className="text-xl font-medium text-gray-900">{solution.title}</h3>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{solution.description}</p>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Capabilities:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {solution.capabilities.map((capability, i) => (
                          <div key={i} className="flex items-center text-sm text-gray-600">
                            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2"></div>
                            {capability}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Data & Analytics Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {dataServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-indigo-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sustainability Metrics */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Sustainability Impact
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sustainabilityFeatures.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="text-3xl font-light text-indigo-600 mb-2">{feature.value}</div>
                  <h4 className="font-medium text-gray-900 mb-2">{feature.metric}</h4>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            Technology Stack
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {technologyStack.map((tech, index) => (
              <Card key={index} className="text-center hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h4 className="font-medium text-gray-900 mb-2">{tech.name}</h4>
                  <Badge variant="outline" className="text-xs">
                    {tech.category}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-16 bg-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light mb-6">Calculate Your ROI</h2>
          <p className="text-xl text-indigo-200 mb-8">
            See how our AI-powered solutions can reduce costs and improve efficiency for your projects.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-4xl font-light mb-2">15-25%</div>
              <div className="text-indigo-200">Cost Reduction</div>
            </div>
            <div>
              <div className="text-4xl font-light mb-2">30-40%</div>
              <div className="text-indigo-200">Time Savings</div>
            </div>
            <div>
              <div className="text-4xl font-light mb-2">95%+</div>
              <div className="text-indigo-200">Accuracy Improvement</div>
            </div>
          </div>
          <Button 
            onClick={() => setIsQuoteModalOpen(true)}
            size="lg" 
            className="bg-white text-indigo-900 hover:bg-gray-100"
          >
            Calculate Your Savings
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Ready to Build Smarter?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Transform your construction projects with AI-powered insights and sustainable building solutions.
          </p>
          <Button 
            onClick={() => setIsQuoteModalOpen(true)}
            size="lg" 
            className="bg-gray-900 hover:bg-gray-800"
          >
            Start Your Smart Building Journey
          </Button>
        </div>
      </section>

      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </div>
  );
};

export default SmartBuildingPage;